import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Activity, Copy, GitBranch, Server, ShieldCheck, UserRound } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const GET_STATUS = gql`
  query GetStatus {
    status {
      serverName
      userAuthenticated
      userName
      version
    }
  }
`;

type StatusQueryData = {
  status: {
    serverName: string;
    userAuthenticated: boolean;
    userName: string;
    version: string;
  };
};

export function StatusPage() {
  const { loading, error, data } = useQuery<StatusQueryData>(GET_STATUS);
  const [authCookie, setAuthCookie] = useState<string>('');

  React.useEffect(() => {
    const cookies = document.cookie.split(';');
    const auth = cookies.find(cookie => cookie.trim().startsWith('Authorization='));
    setAuthCookie(auth ? auth.split('=')[1] : 'Not found');
  }, []);

  const copyToClipboard = () => {
    void navigator.clipboard.writeText(authCookie);
  };

  if (loading) {
    return (
      <div className="p-2">
        <Card>
          <CardContent className="flex min-h-32 items-center justify-center text-sm text-[var(--neutral-gray-80)]">
            Loading system status…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !data?.status) {
    return (
      <div className="p-2">
        <Card className="border-[var(--semantic-error-border-soft)]">
          <CardContent className="flex min-h-32 items-center justify-center text-sm text-[var(--semantic-error)]" role="alert">
            System status is unavailable.
          </CardContent>
        </Card>
      </div>
    );
  }

  const { serverName, userAuthenticated, userName, version } = data.status;
  const statusItems = [
    { id: 'server', label: 'Server', value: serverName || 'Unavailable', icon: Server },
    { id: 'user', label: 'Signed-in user', value: userName || 'Unavailable', icon: UserRound },
    { id: 'version', label: 'Application version', value: version || 'Unavailable', icon: GitBranch },
  ];

  return (
    <div className="space-y-4 p-2">
      <Card>
        <CardHeader className="border-b border-[var(--neutral-gray-30)] pb-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-[var(--azure-primary)]" aria-hidden="true" />
                System Status
              </CardTitle>
              <CardDescription>Current application connection and session details.</CardDescription>
            </div>
            <Badge
              variant="outline"
              className={userAuthenticated
                ? 'border-[var(--semantic-success-border-soft)] bg-[var(--semantic-success-soft)] text-[var(--semantic-success)]'
                : 'border-[var(--semantic-warning-border-soft)] bg-[var(--semantic-warning-soft)] text-[var(--semantic-warning)]'}
            >
              <ShieldCheck className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              {userAuthenticated ? 'Authenticated' : 'Not authenticated'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="grid gap-3 pt-4 md:grid-cols-3">
          {statusItems.map(item => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] p-4"
              >
                <div className="flex items-center gap-2 text-sm text-[var(--neutral-gray-80)]">
                  <Icon className="h-4 w-4 text-[var(--azure-primary)]" aria-hidden="true" />
                  {item.label}
                </div>
                <p className="mt-2 break-words text-base font-semibold text-[var(--neutral-gray-120)]">{item.value}</p>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Session credential</CardTitle>
          <CardDescription>Current authorization cookie value for this browser session.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <code
              id="system-status-auth-cookie"
              className="min-h-8 flex-1 break-all rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] px-3 py-2 text-xs text-[var(--neutral-gray-100)]"
            >
              {authCookie || 'Not found'}
            </code>
            <Button
              id="system-status-copy-auth-cookie"
              type="button"
              variant="outline"
              size="sm"
              onClick={copyToClipboard}
              className="h-8 shrink-0"
            >
              <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
              Copy
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

